import { IHome } from "@src/components/Home";
import React from "react";
import { useQuery } from "@apollo/client";
import ClientOnly from "@src/components/ClientOnly";
import Floater from "../Floater";
import { shimmer, toBase64 } from "@src/utils/shimmer";
import image from "../../../../public/images/image13.jpeg";
import Image from "next/image";
import { motion } from "framer-motion";
import Footer from "../Footer";
import Vendors from "../Vendors";
import Parts from "../Parts";
import Backstory from "../Backstory";
import MarqueeComp from "../MarqueeComp";
import Quotes from "../Quotes";
import Masonry from "../Masonry";
import Dialog from "src/components/Dialog";
import AsoebiModal from "@src/components/AsoebiModal";
import { useEventParts } from "@src/hooks/useEventParts";
import { useHome } from "@src/hooks/useHome";
import WeddingParty from "../WeddingParty";
import FAQ from "../FAQ";
import Recommendations from "../Recommendations";
import {
  Event_PreferencesQuery,
  Event_PreferencesQueryVariables,
} from "@src/@types/api.d";
import { GetEventPreferences } from "@src/graphql/queries";

const Home: React.FC<IHome> = ({
  slug,
  eventBySlugData,
  eventId,
  eventPartsData,
  backStoryData,
  eventVendorData,
  albumDetailsData,
  heroImageData,
  hasWallet,
  getEventStreamersData,
  liveSchedules,
  eventUuid,
  weddingPartyData,
  faqData,
  recommendationData,
  inviteResult: initialInviteResult,
}) => {
  const {
    accessToken,
    inviteResult,
    getAsoebiData,
    openAsoebi,
    setOpenAsoebi,
    isInvitedToEvent,
  } = useHome({
    slug,
    eventBySlugData,
    eventId,
    eventPartsData,
    backStoryData,
    eventVendorData,
    albumDetailsData,
    heroImageData,
    hasWallet,
    getEventStreamersData,
    liveSchedules,
    eventUuid,
    weddingPartyData,
    faqData,
    recommendationData,
    inviteResult: initialInviteResult,
  });

  const { data: eventPreferencesData } = useQuery<
    Event_PreferencesQuery,
    Event_PreferencesQueryVariables
  >(GetEventPreferences, {
    variables: { eventId: eventUuid as string },
    skip: !eventUuid,
  });

  const shouldShowFaq = eventPreferencesData?.event_preferences?.faq ?? false;
  const shouldShowRecommendations =
    eventPreferencesData?.event_preferences?.recommendations ?? false;
  const { parts } = useEventParts({
    eventPartsData,
    liveSchedules,
    eventTimezone: eventBySlugData?.eventBySlug?.eventTimezone,
    isInvitedToEvent,
  });

  return (
    <ClientOnly>
      <Floater
        accessToken={accessToken}
        eventId={eventId}
        eventUuid={eventUuid}
        slugData={eventBySlugData}
        hasWallet={hasWallet}
        getEventStreamersData={getEventStreamersData}
        liveSchedules={liveSchedules}
        access_control={
          eventBySlugData?.eventBySlug?.event_preference?.access_control ??
          false
        }
        qr_code={inviteResult?.signupByInviteLink?.invitation?.qr_code ?? ""}
        getAsoebiData={getAsoebiData}
        admits={inviteResult?.signupByInviteLink?.invitation?.admits ?? 1}
        invitation={inviteResult?.signupByInviteLink?.invitation}
        isInvitedToEvent={isInvitedToEvent}
      />
      <div className="flex flex-col relative">
        <section className="w-full bg-[#EBE3CE]">
          <div className="px-6">
            <div className="flex items-center justify-center sm:justify-between text-sm mb-4 py-3 border-b border-b-[#4B4838]">
              <h2 className="font-neue uppercase text-[#4B4838]">
                {eventBySlugData?.eventBySlug?.hash_tag}
              </h2>
              {/* <h2 className="font-neue uppercase">
                {eventPartsData?.eventParts[0]?.address}
              </h2> */}
            </div>
          </div>

          <div className="flex flex-col relative">
            <div className="relative h-[900px] w-full bg-slate-300 overflow-hidden">
              <motion.div
                initial={{ scale: 1 }}
                animate={{
                  scale: 1.1,
                  transition: {
                    delay: 0.3,
                    duration: 0.8,
                    ease: [0.79, 0.14, 0.15, 0.86],
                  },
                }}
                className={
                  "relative top-0 bottom-0 right-0 left-0 w-full h-full after:absolute after:w-full after:h-full after:bg-black/40"
                }
              >
                <Image
                  placeholder="blur"
                  loading="eager"
                  layout="fill"
                  blurDataURL={`data:image/svg+xml;base64,${toBase64(
                    shimmer(700, 475)
                  )}`}
                  src={
                    !!heroImageData?.getHeroImage
                      ? heroImageData?.getHeroImage
                      : image
                  }
                  alt="gallery-image"
                  className="object-cover object-top"
                />
              </motion.div>
            </div>
            <div className="absolute w-full bottom-[30%] md:bottom-[45%] py-8 gap-8 flex items-center flex-col text-center">
              <h3 className="font-calabassas text-[#EBE3CE] text-[3rem] md:text-[4.25rem]">
                {eventBySlugData?.eventBySlug?.welcome_msg}
              </h3>
              <span className="font-neue text-[#EBE3CE] md:text-[1.25rem] uppercase">
                {eventBySlugData?.eventBySlug?.hash_tag}
              </span>
            </div>
          </div>
        </section>

        <Quotes />

        <Masonry
          eventSlug={slug}
          eventId={eventId}
          albumDetailsData={albumDetailsData}
        />

        <Parts
          liveSchedules={liveSchedules}
          eventId={eventId}
          parts={parts}
          eventPartsData={eventPartsData}
          eventTimezone={eventBySlugData?.eventBySlug?.eventTimezone}
          isInvitedToEvent={isInvitedToEvent}
        />

        {shouldShowFaq && faqData && (
          <FAQ eventId={eventId} faqData={faqData} />
        )}

        {shouldShowRecommendations && recommendationData && (
          <Recommendations
            eventId={eventId}
            recommendationData={recommendationData}
          />
        )}

        {accessToken && (getAsoebiData?.getAseobi?.length ?? 0) > 0 && (
          <div className="py-8 border-y border-y-[#8C7756] bg-[#8C7756]">
            <div className="p-4 lg:py-[7rem] max-w-full rounded-[33px] h-full w-full flex flex-col items-center justify-end gap-6">
              <h3 className="text-[#EBE3CE] font-calabassas text-3xl lg:text-6xl">
                OUR ASO-EBI
              </h3>
              <p className="text-[#EBE3CE] font-neue text-center text-[1.25rem] md:text-[1.9rem] font-[600] max-w-[29rem] px-8">
                The beautiful attires for our wedding
              </p>
              <Dialog
                trigger={
                  <button className="py-[0.25rem] font-[500] uppercase text-[#EBE3CE]  border-solid hover:bg-[#A99F8F]/10 mt-8d px-4 !border-[1.5px] !border-[#EBE3CE] rounded-[0.875rem]">
                    GET YOUR ASO-EBI
                  </button>
                }
                open={openAsoebi}
                onOpenChange={setOpenAsoebi}
              >
                <AsoebiModal eventUuid={eventUuid} setOpen={setOpenAsoebi} />
              </Dialog>
            </div>
          </div>
        )}

        <Backstory backStoryData={backStoryData} />

        <WeddingParty eventId={eventId} weddingPartyData={weddingPartyData} />

        <ClientOnly>
          <MarqueeComp eventId={eventId} slugData={eventBySlugData} />
        </ClientOnly>

        <Vendors eventId={eventId} eventVendorData={eventVendorData} />

        {/* <section className="w-full">
          <div className="rounded-[18px] bg-slate-200  h-[300px]  relative overflow-hidden">
            <motion.div
              initial={{ scale: 1 }}
              animate={{
                scale: 1.1,
                transition: {
                  delay: 0.3,
                  duration: 0.8,
                  ease: [0.79, 0.14, 0.15, 0.86],
                },
              }}
              className={
                "relative top-0 bottom-0 right-0 grayscale  left-0 w-full h-full after:absolute after:w-full after:h-full after:bg-black/60"
              }
            >
              <Image
                placeholder="blur"
                loading="eager"
                layout="fill"
                blurDataURL={`data:image/svg+xml;base64,${toBase64(
                  shimmer(700, 475)
                )}`}
                src={image}
                alt="gallery-image"
                className="object-cover "
              />
            </motion.div>
            <div className="inset-0 flex  text-white flex-col items-center justify-center absolute">
              <span className="font-bold font-lovera text-[1.25rem] md:text-[1.5rem]">
                SHARE THE
              </span>
              <span className="font-lovera text-[2rem] md:text-[3rem] font-bold leading-[2.5rem]">
                Moment
              </span>
              <span className="font-cursive italic font-[700]">
                {eventBySlugData?.eventBySlug?.hash_tag}
              </span>
              <div className="flex justify-center inset-x-0 bottom-4  absolute">
                <Link href={`#`} passHref>
                  <a target="_blank" className="mt-8 relative">
                    <button className="py-[0.25rem]   text-white  w-max z-10 font-[500] uppercase border-solid hover:bg-white/10 px-4 !border-2 !border-white rounded-[0.875rem]">
                      Add PICTURES
                    </button>
                  </a>
                </Link>
              </div>
            </div>
          </div>
        </section> */}
        {/* <section className="w-full">
          <p className="text-center text-[#7E6F5D] font-creattion text-[4rem]">
            made with love by{" "}
            <Link href={process.env.NEXT_PUBLIC_API_LANDING_URL!} passHref>
              <a target="_blank" className="hover:underline">
                Inawo
              </a>
            </Link>
          </p>
        </section> */}

        <Footer />
      </div>
    </ClientOnly>
  );
};

export default Home;
